import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
  darken,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import WarningIcon from '@mui/icons-material/Warning'
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import moment from 'moment'
import { TenantSettingsContext } from '../../../App'
import API from '../../../services/API'
import { useNavigate } from 'react-router-dom'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'

const CleaningSchedules = () => {
  const [data, setData] = useState({})
  const [dataLoading, setDataLoading] = useState(true)
  const [deepCleansDue, setDeepCleansDue] = useState(0)
  const [deepCleansOverdue, setDeepcleansOverdue] = useState(0)
  const [cleanVehicles, setCleanVehicles] = useState(0)
  const [filterMode, setFilterMode] = useState(null)

  const navigate = useNavigate()
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  useEffect(() => {
    API.get('/vehicleDashboardData')
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setDeepCleansDue(res.data.dueCleans)
          setDeepcleansOverdue(res.data.overdueCleans)
          setCleanVehicles(res.data.cleanVehicles)
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setDataLoading(false))
  }, [])

  const classifyVehicle = (v) => {
    const freq = orgSettings?.deepCleanFrequency ?? 12
    const now = moment()

    const last = v.lastDeepClean?.end ? moment(v.lastDeepClean.end) : null

    if (!last) {
      return 'OVERDUE'
    }

    const dueDate = last.clone().add(freq, 'weeks')
    const warnDate = last.clone().add(freq - 1, 'weeks')

    if (now.isAfter(dueDate)) {
      return 'OVERDUE'
    }

    if (now.isAfter(warnDate)) {
      return 'DUE'
    }

    return 'CLEAN'
  }

  const getFilteredVehicles = () => {
    if (!data?.vehicles) return []

    return data.vehicles.filter((v) => {
      const state = classifyVehicle(v)

      if (filterMode === 'OVERDUE') return state === 'OVERDUE'
      if (filterMode === 'DUE') return state === 'OVERDUE' || state === 'DUE'
      if (filterMode === 'CLEAN') return state === 'CLEAN'

      return true // no filter
    })
  }

  const StatCard = ({
    label,
    number,
    icon: Icon,
    color,
    onClick,
    selected,
  }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: { xs: 1, lg: 2.5 },
        borderRadius: 3,
        background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
        border: selected ? '2px solid #1976d2' : '1px solid #e5e5e5',
        flex: 1,
        cursor: 'pointer',
        transition: '0.2s',
        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            p: { xs: 0, lg: 1.8 },
            borderRadius: 3,
            backgroundColor: color,
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Stack
          direction={{ xs: 'row', lg: 'column' }}
          gap={{ xs: 2, lg: 0 }}
          alignItems={{ xs: 'center', lg: 'flex-start' }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {dataLoading ? '—' : number}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  )

  return (
    <Stack spacing={{ xs: 1, lg: 3 }}>
      {/* Page header */}
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent={{ xs: 'center', lg: 'space-between' }}
        alignItems="center"
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }} textAlign="center">
          Vehicle Cleaning Overview
        </Typography>

        <Button
          variant="contained"
          startIcon={<LocalCarWashIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 2.5,
            py: 1,
          }}
          color="anMainBlue"
          onClick={() => navigate('/vehicle-clean')}
        >
          Record a Clean
        </Button>
      </Stack>

      {/* Stats */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={{ xs: 1, lg: 2 }}>
        <StatCard
          label="Deep Cleans Due"
          number={deepCleansDue}
          icon={deepCleansDue ? WarningIcon : CheckCircleIcon}
          color={deepCleansDue ? '#ff6b6b' : '#4caf50'}
          selected={filterMode === 'DUE'}
          onClick={() => setFilterMode(filterMode === 'DUE' ? null : 'DUE')}
        />

        <StatCard
          label="Deep Cleans Overdue"
          number={deepCleansOverdue}
          icon={deepCleansOverdue ? WarningIcon : CheckCircleIcon}
          color={deepCleansOverdue ? '#e74c3c' : '#4caf50'}
          selected={filterMode === 'OVERDUE'}
          onClick={() =>
            setFilterMode(filterMode === 'OVERDUE' ? null : 'OVERDUE')
          }
        />

        <StatCard
          label="Deep Cleans Completed"
          number={cleanVehicles}
          icon={CheckCircleIcon}
          color={'#4caf50'}
          selected={filterMode === 'CLEAN'}
          onClick={() => setFilterMode(filterMode === 'CLEAN' ? null : 'CLEAN')}
        />
      </Stack>

      {/* DataGrid wrapper */}
      <Box
        component={Paper}
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          background: '#ffffff',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Cleaning Schedule Details
        </Typography>
        <Box>
          <DataGrid
            slots={{
              noRowsOverlay: () => (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  p={2}
                >
                  <Alert severity="info">
                    No vehicles to schedule yet. Register your fleet under
                    Fleet & Equipment → Vehicles, then record cleans with the
                    button above. Clean checklists and deep-clean frequency
                    are set in Site Configuration → Fleet.
                  </Alert>
                </Box>
              ),
            }}
            rows={getFilteredVehicles()}
            columns={[
              {
                headerName: 'Vehicle',
                field: 'vehicle',
                flex: 0.6,
                valueGetter: (_, row) => row.callSign,
                minWidth: 120,
              },
              {
                headerName: 'Last Deep Clean',
                headerAlign: 'center',
                align: 'center',
                field: 'lastDeepClean',
                flex: 1,
                minWidth: 120,
                renderCell: ({ row }) => {
                  if (!row.lastDeepClean?.end) return '—'
                  const last = moment(row.lastDeepClean.end)
                  const freq = orgSettings?.deepCleanFrequency ?? 0
                  const due = last.clone().add(freq, 'weeks')
                  const warn = last.clone().add(freq - 1, 'weeks')

                  let color = '#a7ffcc'
                  if (due < moment()) color = '#ffb3b3'
                  else if (warn < moment()) color = '#feffb6'

                  return (
                    <Chip
                      label={last.format('DD/MM/YYYY')}
                      sx={{
                        backgroundColor: color,
                        width: '100%',
                        justifyContent: 'center',
                        fontSize: 14,
                      }}
                    />
                  )
                },
              },
              {
                headerName: 'Deep Clean Due',
                headerAlign: 'center',
                field: 'deepCleanDue',
                align: 'center',
                minWidth: 120,
                flex: 1,
                renderCell: ({ row }) => {
                  if (!row.lastDeepClean?.end) return 'None Recorded'
                  const last = moment(row.lastDeepClean.end)
                  const freq = orgSettings?.deepCleanFrequency ?? 0
                  const due = last.clone().add(freq, 'weeks')

                  let color = '#a7ffcc'
                  if (due < moment()) color = '#ffb3b3'
                  else if (due.clone().subtract(1, 'week') < moment())
                    color = '#feffb6'

                  return (
                    <Chip
                      label={due.format('DD/MM/YYYY')}
                      sx={{
                        backgroundColor: color,
                        width: '100%',
                        justifyContent: 'center',
                        fontSize: 14,
                      }}
                    />
                  )
                },
              },
              {
                headerName: 'Last Clean',
                field: 'lastClean',
                align: 'center',
                headerAlign: 'center',
                flex: 1,
                minWidth: 120,
                renderCell: ({ row }) =>
                  row.lastClean?.end
                    ? moment(row.lastClean.end).format('DD/MM/YYYY')
                    : '—',
              },
              {
                headerName: 'Status',
                field: 'status',
                flex: 0.7,
                headerAlign: 'center',
                align: 'center',
                minWidth: 120,
                renderCell: ({ row }) => {
                  let bg = 'anGreen.main'
                  if (row.status === 'Out of Service') {
                    bg = 'anRed.main'
                  }

                  return (
                    <Chip
                      label={row.status ?? 'Out of Service'}
                      sx={{
                        backgroundColor: bg,
                        color: 'white',
                        fontWeight: 600,
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    />
                  )
                },
              },
            ]}
            getRowId={(row) => row._id}
            disableRowSelectionOnClick
            onRowClick={({ row }) =>
              navigate('/fleet/' + row._id + '?opentab=cleans')
            }
            sx={{
              borderRadius: 2,
              border: 'none',
            }}
          />
        </Box>
      </Box>
    </Stack>
  )
}

export default CleaningSchedules
